import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import ActionMenu from 'components/ActionMenu';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import DownloadIcon from 'icons/Download.icon';
import { getCanDownloadNode } from 'utils';
import { useTranslation } from 'react-i18next';
import useMatchMedia from 'hooks/useMatchMedia';
import useNodes from 'hooks/useNodes';
import { Link } from 'react-router-dom';
import ArrowupIcon from 'icons/Arrowup.icon';
import ShareableLinkDocument from '../ShareableLinkDocument/ShareableLinkDocument.component';
import ShareableLinkDataContext from 'router/subrouters/ShareableLink/providers/ShareableLinkData/ShareableLinkData.context';
import ShareableLinkDocumentsContext from 'router/subrouters/ShareableLink/providers/ShareableLinkDocuments/ShareableLinkDocuments.context';
import ShareableLinkDocumentsListEmptyState from './components/ShareableLinkDocumentsListEmptyState';

import './ShareableLinkDocumentsList.styles.scss';

type ShareableLinkDocumentsListProps = {
  className?: string;
};

const ShareableLinkDocumentsList: React.FC<ShareableLinkDocumentsListProps> = (
  props,
) => {
  const { className } = props;

  const {
    shareableLink: {
      _id: shareableLinkId,
      dataRoom: { name: dataRoomName },
    },
  } = useContext(ShareableLinkDataContext);
  const {
    currentlyDisplayedNodes,
    selected,
    isSelectedMode,
    baseDocsPath,
    documentRoute,
    isLoading,
    isEmptyOnThatLevel,
    download,
    removeAll,
    updateSelected,
    onSelect,
  } = useContext(ShareableLinkDocumentsContext);

  const classes = classNames('shareable-link-documents-list', className);

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const { getCopyByNodeType } = useNodes(selected);

  const selectedLabel = getCopyByNodeType(
    'itemsSelected',
    'foldersSelected',
    'filesSelected',
    selected.length,
  );

  const showLevelUp = useMemo(
    () => !!documentRoute.length,
    [documentRoute.length],
  );

  const prevNode = useMemo(
    () =>
      documentRoute.length > 1 ? documentRoute[documentRoute.length - 2] : null,
    [documentRoute],
  );

  const handleDownload = useCallback(
    (nodes: DataRoomNode[]) => {
      download(nodes);
      removeAll();
    },
    [download, removeAll],
  );

  const getActions = useCallback(
    (nodes: DataRoomNode[]) => {
      const canDownloadNodes = nodes.every(getCanDownloadNode);

      return [
        ...(canDownloadNodes
          ? [
              {
                label: t('download'),
                icon: <DownloadIcon />,
                onClick: () => handleDownload(nodes),
              },
            ]
          : []),
      ];
    },
    [t, handleDownload],
  );

  const actionMenuOptions = useMemo(
    () => getActions(selected),
    [selected, getActions],
  );

  return (
    <div className={classes}>
      {isEmptyOnThatLevel && <ShareableLinkDocumentsListEmptyState />}

      {!isLoading &&
        currentlyDisplayedNodes.map((node) => {
          const isSelected = selected.some(({ _id }) => _id === node._id);

          return (
            <div key={node?._id} className="document-wrapper">
              <ShareableLinkDocument
                node={node}
                dataRoomName={dataRoomName}
                baseDocsPath={baseDocsPath}
                shareableLinkId={shareableLinkId}
                isSelected={isSelected}
                isSelectedMode={isSelectedMode}
                onSelect={isPhablet ? updateSelected : onSelect}
                dropdownOptions={getActions(
                  selected?.length ? selected : [node],
                )}
              />
            </div>
          );
        })}

      {!isLoading && showLevelUp && (
        <Link
          className="link-back"
          to={`${baseDocsPath}${prevNode ? prevNode._id : ''}`}
        >
          <ArrowupIcon />
          {t('backUpALevel')}
        </Link>
      )}

      {isSelectedMode && !!actionMenuOptions?.length && (
        <ActionMenu
          className={classes}
          label={t(selectedLabel, { count: selected.length })}
          actions={actionMenuOptions}
          onClose={removeAll}
        />
      )}
    </div>
  );
};

export default ShareableLinkDocumentsList;
